import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import Layout from '../../layout/Layout';
import Card from '../../components/Card/Card';
import Input from '../../components/Input/Input';
import Text from '../../components/Text/Text';
import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import AlertType from '../../common/alertType';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import { permissions } from '../../common/permissionGroup';
import { IconArrowDown } from '../../assets/icon';
import {
  getSupplierOrderSettings,
  updateSupplierOrderSettings
} from '../../services/supplierOrders';

const recipientFields = [
  ['name', 'Recipient name'],
  ['phone_number', 'Phone'],
  ['address', 'Address'],
  ['address_2', 'Address line 2'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
  ['country', 'Country'],
];

const emptyRecipient = {
  name: '',
  address: '',
  address_2: '',
  city: '',
  state: '',
  country: '',
  zip: '',
  phone_number: '',
  is_shop_address: true,
};

const getErrorMessage = (error) => {
  const validationErrors = error?.errors ? Object.values(error.errors).flat() : [];
  return validationErrors[0]
    || error?.message
    || error?.data?.message
    || 'Could not save supplier settings.';
};

const SupplierSettings = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [settings, setSettings] = useState();
  const [recipient, setRecipient] = useState(emptyRecipient);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState();
  const [addressExpanded, setAddressExpanded] = useState(false);

  useEffect(() => {
    let active = true;

    getSupplierOrderSettings()
      .then((result) => {
        if (!active) return;
        setSettings(result);
        setRecipient(result.default_recipient || emptyRecipient);
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const handleSave = () => {
    if (!settings) return;

    setSaving(true);
    setError(undefined);
    updateSupplierOrderSettings({
      ...recipient,
      google_sheet_id: settings.google_sheet_id,
      google_sheet_range: settings.google_sheet_range,
      rockauto_order_email: settings.rockauto_order_email,
      rockauto_coupon_code: settings.rockauto_coupon_code,
      keystone_order_email: settings.keystone_order_email,
      meyer_customer_number: settings.meyer_customer_number,
      etristate_shipping_method: settings.etristate_shipping_method,
      rcd_order_email: settings.rcd_order_email,
      rcd_reply_to_email: settings.rcd_reply_to_email,
      rcd_email_template: settings.rcd_email_template,
      disabled_suppliers: settings.disabled_suppliers || [],
    })
      .then((result) => {
        setSettings(result);
        setRecipient(result.default_recipient || recipient);
        enqueueSnackbar('Supplier settings updated', { variant: 'success' });
      })
      .catch((requestError) => setError(getErrorMessage(requestError)))
      .finally(() => setSaving(false));
  };

  const disabledSuppliers = settings?.disabled_suppliers || [];
  const isSupplierEnabled = (supplier) => !disabledSuppliers.includes(supplier);
  const toggleSupplier = (supplier) => setSettings((current) => {
    const disabled = current.disabled_suppliers || [];
    return {
      ...current,
      disabled_suppliers: disabled.includes(supplier)
        ? disabled.filter((item) => item !== supplier)
        : [...disabled, supplier],
    };
  });
  const isInvalid = !recipient.name || !recipient.address || !recipient.city
    || !recipient.state || !recipient.country || !recipient.zip || !recipient.phone_number
    || !settings?.google_sheet_id || !settings?.google_sheet_range
    || (isSupplierEnabled('RockAuto') && !settings?.rockauto_order_email)
    || (isSupplierEnabled('Etristate') && !settings?.etristate_shipping_method)
    || (isSupplierEnabled('RCD') && (
      !settings?.rcd_order_email || !settings?.rcd_email_template
    ));
  const integrations = settings?.integrations || [{
    supplier: settings?.supplier || 'Turn14',
    configured: true,
    capabilities: settings?.capabilities || {},
  }];
  const addressSummary = [
    recipient.name,
    recipient.address,
    recipient.address_2,
    [recipient.city, recipient.state, recipient.zip].filter(Boolean).join(' '),
    recipient.country,
  ].filter(Boolean).join(', ');
  const breadcrumbs = {
    pageTitle: 'Supplier Settings',
    history: [
      { title: 'Home', onClick: () => navigate('/') },
      { title: 'Supplier Settings' },
    ],
  };

  return (
    <Layout breadcrumbs={breadcrumbs} pageTitle="Supplier Settings">
      <PermissionsGate
        can={permissions.SUPPLIER_ORDERS_CREATE}
        errorComponent={<AccessDenied />}
      >
        <div className="w-full max-w-[980px]">
          <Card
            showHeader
            headerTitle="General order settings"
            headerRight={(
              <Button
                text="Save settings"
                onClick={handleSave}
                disabled={loading || saving || isInvalid}
              />
            )}
            styleBodyContainer="pb-[20px] md:pb-[30px]"
          >
            {error && <Alert text={error} type={AlertType.warning} />}
            {loading && !settings ? (
              <Text text="Loading supplier settings..." muted />
            ) : (
              <div className="flex flex-col gap-[24px]">
                <section className="border border-whiteHover dark:border-[#415a77]/60 rounded-[8px]">
                  <button
                    type="button"
                    aria-expanded={addressExpanded}
                    aria-controls="default-shipping-address-fields"
                    className="w-full flex items-center justify-between gap-[16px] p-[14px]
                    text-left hover:bg-background-main dark:hover:bg-[#415a77]/20 rounded-[8px]"
                    onClick={() => setAddressExpanded((expanded) => !expanded)}
                  >
                    <span className="min-w-0">
                      <span className="block text-[14px] font-semibold text-text-main dark:text-secondary">
                        Default shipping address
                      </span>
                      <span className="block mt-[3px] text-[12px] text-text-muted break-words">
                        {addressSummary || 'Used for orders from all suppliers'}
                      </span>
                    </span>
                    <IconArrowDown
                      className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200
                      ${addressExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {addressExpanded && (
                    <div
                      id="default-shipping-address-fields"
                      className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] px-[14px] pb-[14px]
                      border-t border-whiteHover dark:border-[#415a77]/60 pt-[14px]"
                    >
                      {recipientFields.map(([field, label]) => (
                        <div className="flex flex-col gap-[5px]" key={field}>
                          <Text text={label} muted />
                          <Input
                            value={recipient[field] || ''}
                            disabled={saving}
                            onChange={({ target: { value } }) => setRecipient((current) => ({
                              ...current,
                              [field]: value,
                            }))}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="Google Sheets" bold />
                  <div className="grid grid-cols-1 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Spreadsheet ID" muted />
                      <Input
                        value={settings?.google_sheet_id || ''}
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          google_sheet_id: value,
                        }))}
                      />
                    </div>
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Range" muted />
                      <Input
                        value={settings?.google_sheet_range || ''}
                        placeholder="CO!A:D"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          google_sheet_range: value,
                        }))}
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="RockAuto" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Order email" muted />
                      <Input
                        value={settings?.rockauto_order_email || ''}
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          rockauto_order_email: value,
                        }))}
                      />
                    </div>
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Default coupon code" muted />
                      <Input
                        value={settings?.rockauto_coupon_code || ''}
                        placeholder="Optional"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          rockauto_coupon_code: value,
                        }))}
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="Keystone" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Drop-ship email" muted />
                      <Input
                        value={settings?.keystone_order_email || ''}
                        placeholder="Optional"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          keystone_order_email: value,
                        }))}
                      />
                      <Text
                        text="Optional. Keystone can create an order without an email address."
                        muted
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="Meyer" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Customer number" muted />
                      <Input
                        value={settings?.meyer_customer_number || ''}
                        placeholder="Defaults to MEYER_CLIENT_ID"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          meyer_customer_number: value,
                        }))}
                      />
                      <Text
                        text="Meyer CustomerNumber may differ from the API username."
                        muted
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="Etristate" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Default shipping code" muted />
                      <Input
                        value={settings?.etristate_shipping_method || ''}
                        placeholder="2DAY"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          etristate_shipping_method: value,
                        }))}
                      />
                      <Text
                        text="The code is sent in the Etristate ship comment for every order."
                        muted
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="RCD email orders" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    <div className="flex flex-col gap-[5px]">
                      <Text text="To email" muted />
                      <Input
                        value={settings?.rcd_order_email || ''}
                        placeholder="sales@rcdofaz.com"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          rcd_order_email: value,
                        }))}
                      />
                      <Text text="RCD orders are sent to this address instead of an API." muted />
                    </div>
                    <div className="flex flex-col gap-[5px]">
                      <Text text="Reply-To" muted />
                      <Input
                        value={settings?.rcd_reply_to_email || ''}
                        placeholder="info@bilsteinlifts.com"
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          rcd_reply_to_email: value,
                        }))}
                      />
                      <Text text="Optional address for replies from RCD." muted />
                    </div>
                    <div className="flex flex-col gap-[5px] sm:col-span-2">
                      <Text text="Email message template" muted />
                      <Input
                        multiline
                        rows={12}
                        value={settings?.rcd_email_template || ''}
                        disabled={saving}
                        onChange={({ target: { value } }) => setSettings((current) => ({
                          ...current,
                          rcd_email_template: value,
                        }))}
                      />
                      <Text
                        text={`Available placeholders: {{po_number}}, {{ordered_part_id}},
                          {{items}}, {{ship_to}}, {{comment}}.`}
                        muted
                      />
                    </div>
                  </div>
                </section>

                <section className="border-t border-whiteHover dark:border-[#415a77]/60 pt-[18px]">
                  <Text text="Supplier integrations" bold />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mt-[10px]">
                    {integrations.map(({ supplier, configured, capabilities = {} }) => {
                      const enabled = isSupplierEnabled(supplier);
                      return (
                        <div
                          key={supplier}
                          className={`border rounded-[8px] p-[12px]
                            ${enabled
                            ? 'border-whiteHover dark:border-[#415a77]/60'
                            : 'border-text-muted/30 bg-background-main/60 dark:bg-[#1b263b]/40'}`}
                        >
                          <div className="flex items-center justify-between gap-[12px] mb-[8px]">
                            <div>
                              <Text text={supplier} bold />
                              <Text text={configured ? 'Configured' : 'Not configured'} muted />
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={enabled}
                              aria-label={`${enabled ? 'Disable' : 'Enable'} ${supplier}`}
                              disabled={saving}
                              className={`relative w-[44px] h-[24px] rounded-full shrink-0 transition-colors
                                disabled:opacity-50 ${enabled ? 'bg-primary' : 'bg-text-muted/40'}`}
                              onClick={() => toggleSupplier(supplier)}
                            >
                              <span
                                className={`absolute top-[3px] left-[3px] w-[18px] h-[18px] rounded-full bg-white
                                  shadow-sm transition-transform ${enabled ? 'translate-x-[20px]' : 'translate-x-0'}`}
                              />
                            </button>
                          </div>
                          <Text text={enabled ? 'Available for ordering' : 'Disabled'} muted />
                          <Text text={`Quotes: ${capabilities.quotes ? 'yes' : 'no'}`} muted />
                          <Text
                            text={`Workflow: ${(capabilities.order_workflow || 'not configured').replace(/_/g, ' ')}`}
                            muted
                          />
                          <Text
                            text={`Split warehouses: ${capabilities.split_shipments ? 'yes' : 'no'}`}
                            muted
                          />
                          {capabilities.default_shipping_method && (
                            <Text
                              text={`Default shipping: ${capabilities.default_shipping_method}`}
                              muted
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}
          </Card>
        </div>
      </PermissionsGate>
    </Layout>
  );
};

export default SupplierSettings;
